// checkout-resume.js — save + resume a pending Free-tier action
// across a Stripe checkout round-trip (PR G, 2026-05-15).
//
// Problem: Free user clicks "+ New workspace" → 402 → upgrade
// modal → Stripe Checkout → redirects to ?billing=success →
// dashboard refreshes. By default, the workspace name they typed
// is gone — they have to click "+ New workspace" again and
// re-type. Same for "+ New role" + scope selections.
//
// Solution (mirroring the existing ac-pending-invites pattern in
// billing.js): before redirecting to Stripe, save the pending
// intent to sessionStorage with a timestamp. On the billing=success
// return, the billing.js handler reads the saved intent, validates
// freshness (<30 min), and auto-fires the originally-blocked
// create. The user lands back on the dashboard with their
// workspace/role already created.
//
// Three intents supported:
//   - workspace: { name: "Engineering" }
//   - role:      { name: "Compliance Reviewer", scopes: [...] }
//   - invitations: existing flow, handled by billing.js directly
//
// Each intent has a max-age and validation step. Stale or
// malformed payloads are silently dropped on read so a never-
// completed flow doesn't fire on a later unrelated checkout.

const STORAGE_KEY = "ac-pending-checkout-resume";
const STALE_MS = 30 * 60 * 1000; // 30 minutes

// saveCheckoutResume — call BEFORE redirecting to Stripe (i.e.,
// before showUpgradeRequiredFromError fires startCheckout). The
// intent shape:
//   { kind: "workspace", payload: { name } }
//   { kind: "role", payload: { name, scopes } }
//
// sessionStorage is per-tab. A user with multiple tabs opening
// checkout simultaneously will overwrite each other's intent,
// but that's an edge case the existing ac-pending-invites pattern
// also accepts.
export function saveCheckoutResume(kind, payload) {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ kind, payload, savedAt: Date.now() }),
    );
  } catch {
    // Quota / private mode — drop silently. The user will just
    // have to re-create after upgrading. Not worth a toast.
  }
}

// peekCheckoutResume — returns the validated intent if a fresh one
// exists, null otherwise. Does NOT remove the storage entry. The
// caller must call clearCheckoutResume() after successfully using
// the intent. This split (peek + clear) addresses the PR G sweep
// finding #2: the original consume() removed the entry BEFORE the
// auto-fire create ran, so a network failure or webhook-not-landed
// 402 lost the intent forever. The user got a "couldn't auto-create"
// toast with nothing to retry.
//
// New flow:
//   const intent = peekCheckoutResume();
//   if (!intent) return;
//   try {
//     await api.create(intent);
//     clearCheckoutResume();
//   } catch (e) {
//     // Intent stays in storage. User can refresh or retry; we'll
//     // pick it up next billing=success or on next manual click.
//   }
export function peekCheckoutResume() {
  let raw;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
  } catch {
    return null;
  }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Garbage in storage — clear it so we don't keep hitting the
    // parse error on every billing return.
    clearCheckoutResume();
    return null;
  }
  if (!parsed || typeof parsed !== "object") {
    clearCheckoutResume();
    return null;
  }
  const { kind, payload, savedAt } = parsed;
  // Freshness check: stale payloads from never-completed flows
  // are dropped + cleared. A user who started checkout 2 hours
  // ago, abandoned, and now completes a DIFFERENT checkout
  // shouldn't have their original intent fire.
  if (typeof savedAt !== "number" || Date.now() - savedAt > STALE_MS) {
    clearCheckoutResume();
    return null;
  }
  // Per-kind validation. Defensive: future kinds added here
  // without the corresponding billing.js read handler should
  // fail closed. Don't clear on bad shape — a transient bug in
  // validation shouldn't lose the user's intent if it could be
  // fixed by a code update.
  if (kind === "workspace") {
    if (!payload || typeof payload.name !== "string" || !payload.name.trim()) {
      return null;
    }
    return { kind, payload: { name: payload.name.trim() } };
  }
  if (kind === "role") {
    if (!payload || typeof payload.name !== "string" || !payload.name.trim()) {
      return null;
    }
    if (!Array.isArray(payload.scopes)) return null;
    return {
      kind,
      payload: {
        name: payload.name.trim(),
        scopes: payload.scopes.filter((s) => typeof s === "string"),
      },
    };
  }
  return null;
}

// clearCheckoutResume — remove the saved intent from sessionStorage.
// Call this after the auto-fire create succeeds, or to force-discard
// a stale payload. Swallows errors (private mode, quota).
export function clearCheckoutResume() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Best-effort.
  }
}

// consumeCheckoutResume — DEPRECATED. Kept as an alias for
// callers that haven't been updated to the peek + clear pattern.
// New code should use peekCheckoutResume + clearCheckoutResume so
// auto-fire failures don't drop the intent. This deprecated form
// removes-then-uses, which loses the intent on any error in the
// caller's flow.
export function consumeCheckoutResume() {
  const intent = peekCheckoutResume();
  if (intent) clearCheckoutResume();
  return intent;
}
